import { useCallback, useEffect, useState } from "react";
import useAuth from "./useAuth";
import useWorkMode from "./useWorkMode";
import {
  getOfflineSyncSnapshot,
  hydrateOfflineSync,
  startOfflineSync,
  stopOfflineSync,
  subscribeOfflineSync,
  SYNC_STATUS,
} from "../offline/offlineSyncManager";

/** Mount once in agency/agent layout. Starts/resumes IndexedDB sync. */
export function useOfflineSyncController() {
  const { auth } = useAuth();
  const { isOffline } = useWorkMode();

  useEffect(() => {
    if (!auth?.user) return undefined;
    hydrateOfflineSync(auth.user);
    return undefined;
  }, [auth?.user?.id, auth?.user?._id, auth?.user?.companyId]);

  useEffect(() => {
    if (!isOffline || !auth?.token || !auth?.user) return undefined;
    if (navigator.onLine === false) return undefined;
    startOfflineSync({ token: auth.token, user: auth.user });
    return undefined;
  }, [isOffline, auth?.token, auth?.user?.id, auth?.user?._id]);

  useEffect(() => {
    if (!isOffline || !auth?.token || !auth?.user) return undefined;
    const onOnline = () => {
      startOfflineSync({ token: auth.token, user: auth.user });
    };
    const onOffline = () => {
      stopOfflineSync();
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [isOffline, auth?.token, auth?.user]);
}

export default function useOfflineSync() {
  const { auth } = useAuth();
  const { isOffline } = useWorkMode();
  const [state, setState] = useState(getOfflineSyncSnapshot);

  useEffect(() => subscribeOfflineSync(setState), []);

  const retry = useCallback(() => {
    if (!auth?.token || !auth?.user) return;
    startOfflineSync({ token: auth.token, user: auth.user });
  }, [auth?.token, auth?.user]);

  return {
    ...state,
    isOffline,
    retry,
    canRetry: isOffline && state.status === SYNC_STATUS.FAILED,
  };
}
